import { useState, useEffect, useCallback } from 'react';
import { Clock, Play, PartyPopper, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

/**
 * Countdown component for scheduled releases
 * Shows time remaining until release date, then celebrates!
 */
export function ReleaseCountdown({ releaseDate, title, coverUrl, onPreview, onReleased, variant = 'default' }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(releaseDate));
  const [isReleased, setIsReleased] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const isHero = variant === 'hero';

  // Fire confetti burst
  const fireConfetti = useCallback(() => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Fire from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#8B5CF6', '#06B6D4', '#F59E0B', '#EC4899', '#10B981']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#8B5CF6', '#06B6D4', '#F59E0B', '#EC4899', '#10B981']
      });
    }, 250);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(releaseDate);
      setTimeLeft(newTimeLeft);
      
      // Check if just released
      if (new Date(releaseDate) <= new Date() && !isReleased) {
        setIsReleased(true);
        setShowCelebration(true);
        fireConfetti();
        
        // Notify parent component
        if (onReleased) {
          onReleased();
        }
        
        // Hide celebration after 10 seconds
        setTimeout(() => {
          setShowCelebration(false);
        }, 10000);
        
        clearInterval(timer);
      }
    }, 1000);

    // Check if already released on mount
    if (new Date(releaseDate) <= new Date()) {
      setIsReleased(true);
    }

    return () => clearInterval(timer);
  }, [releaseDate, isReleased, fireConfetti, onReleased]);

  // Show celebration screen when just released
  if (showCelebration) {
    return (
      <div className="glass-card p-8 relative overflow-hidden text-center" data-testid="release-celebration">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 via-cyan-500/30 to-purple-500/30 animate-pulse" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <PartyPopper className="w-8 h-8 text-yellow-400 animate-bounce" />
            <Sparkles className="w-6 h-6 text-cyan-400 animate-pulse" />
            <PartyPopper className="w-8 h-8 text-yellow-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">IT'S HERE!</h2>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            {coverUrl && (
              <img src={coverUrl} alt={title} className="w-24 h-24 rounded-xl shadow-2xl animate-pulse" />
            )}
            <div className="text-left">
              <p className="text-cyan-400 text-sm font-medium">NOW AVAILABLE</p>
              <h3 className="text-2xl font-bold">{title}</h3>
            </div>
          </div>
          
          <button 
            onClick={onPreview}
            className="btn-primary text-lg px-8 py-3 animate-pulse"
            data-testid="play-now-button"
          >
            <Play className="w-5 h-5 mr-2 inline" />
            Play Now
          </button>
        </div>
      </div>
    );
  }

  // Don't show anything if already released (and celebration is done)
  if (isReleased) {
    return null;
  }

  return (
    <div
      className={`glass-card relative overflow-hidden ${isHero ? 'p-6 sm:p-10 border border-purple-500/30 animate-pulse-glow' : 'p-4 sm:p-6'}`}
      data-testid="release-countdown"
    >
      {/* Background glow */}
      <div className={`absolute -top-10 -right-10 rounded-full blur-3xl bg-purple-500/20 ${isHero ? 'w-64 h-64' : 'w-40 h-40'}`} />
      <div className={`absolute -bottom-10 -left-10 rounded-full blur-3xl bg-cyan-500/20 ${isHero ? 'w-64 h-64' : 'w-40 h-40'}`} />
      
      <div className={`relative z-10 flex flex-col items-center gap-4 sm:gap-6 md:flex-row ${isHero ? 'md:gap-10' : ''}`}>
        {/* Cover Art */}
        {coverUrl && (
          <div className="relative group flex-shrink-0">
            <img 
              src={coverUrl} 
              alt={title} 
              className={`rounded-xl object-cover shadow-2xl ${isHero ? 'w-32 h-32 sm:w-40 sm:h-40 md:w-52 md:h-52' : 'w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40'}`}
            />
            {onPreview && (
              <button 
                onClick={onPreview}
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
                data-testid="preview-button"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-500 flex items-center justify-center">
                  <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-1" />
                </div>
                <span className="absolute bottom-2 text-xs font-medium text-white/80">30s Preview</span>
              </button>
            )}
          </div>
        )}
        
        {/* Countdown Info */}
        <div className="text-center md:text-left flex-1">
          <div className="flex items-center justify-center md:justify-start gap-2 text-cyan-400 mb-2">
            <Clock className={isHero ? 'w-5 h-5 animate-pulse' : 'w-4 h-4'} />
            <span className={`font-medium uppercase tracking-wider ${isHero ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>Next Release Dropping In</span>
          </div>
          
          <h3 className={`font-bold mb-3 sm:mb-4 ${isHero ? 'text-2xl sm:text-3xl md:text-4xl gradient-text' : 'text-xl sm:text-2xl md:text-3xl'}`}>{title}</h3>
          
          {/* Countdown Timer */}
          <div className="flex justify-center md:justify-start gap-2 sm:gap-3">
            <TimeUnit value={timeLeft.days} label="Days" big={isHero} />
            <TimeUnit value={timeLeft.hours} label="Hours" big={isHero} />
            <TimeUnit value={timeLeft.minutes} label="Mins" big={isHero} />
            <TimeUnit value={timeLeft.seconds} label="Secs" big={isHero} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TimeUnit({ value, label, big }) {
  return (
    <div className="flex flex-col items-center">
      <div className={`rounded-xl bg-white/5 border border-purple-500/20 flex items-center justify-center transition-transform ${big ? 'w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 hover:scale-105' : 'w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20'}`}>
        <span className={`font-bold text-white ${big ? 'text-2xl sm:text-3xl md:text-4xl' : 'text-xl sm:text-2xl md:text-3xl'}`}>
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[10px] sm:text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</span>
    </div>
  );
}

function calculateTimeLeft(releaseDate) {
  const now = new Date();
  const release = new Date(releaseDate);
  const diff = release - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  };
}

export default ReleaseCountdown;
